import { RiDownload2Line } from '@remixicon/react'

import { cn } from '#app/utils/misc.tsx'

type TemplateDownload = {
	readonly filename: string
	readonly label: string
}

export type Props = {
	readonly templates: readonly TemplateDownload[]
	readonly className?: string
	readonly linkTarget?: string
	readonly linkRel?: string
}

export function TemplateDownloadList({
	templates,
	className,
	linkTarget,
	linkRel,
}: Props) {
	const rel =
		typeof linkRel === 'string'
			? linkRel
			: linkTarget === '_blank'
				? 'noreferrer'
				: undefined

	return (
		<ul
			className={cn(
				'text-muted-foreground list-disc space-y-2 pl-5 text-sm leading-6',
				className,
			)}
		>
			{templates.map((template) => {
				const isAdverse = /adverse-event/.test(template.filename)
				const downloadHref = isAdverse
					? `/mocra/sop-templates/${encodeURIComponent(
							template.filename.replace(/\.md$/i, '.docx'),
						)}`
					: undefined

				return (
					<li key={template.filename}>
						{downloadHref ? (
							<a
								href={downloadHref}
								download
								target={linkTarget}
								rel={rel}
								className="flex items-center gap-1.5 underline-offset-2 transition-colors hover:underline"
							>
								<RiDownload2Line className="text-muted-foreground h-3.5 w-3.5" />
								<span className="text-muted-foreground">{template.label}</span>
							</a>
						) : (
							// Disabled link: prefer explicit onRequestHelp prop, then dialog context,
							// then DOM trigger, then global event.
							<a
								role="button"
								href="#"
								onClick={(e) => {
									e.preventDefault()
									// 3) DOM-based dialog trigger
									try {
										const target = e.currentTarget as HTMLElement
										const container = target.closest('section,article,main,div')
										const trigger = container?.querySelector(
											'[data-slot="dialog-trigger"]',
										) as HTMLElement | null
										if (trigger) {
											trigger.click()
											return
										}
									} catch {
										// ignore
									}
								}}
								className="text-muted-foreground flex cursor-help items-center gap-1.5 opacity-70"
								title="Talk to our team"
							>
								<RiDownload2Line className="text-muted-foreground h-3.5 w-3.5" />
								<span className="text-muted-foreground">{template.label}</span>
							</a>
						)}
					</li>
				)
			})}
		</ul>
	)
}
